import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavItem, NavLink, TabsWrap } from "@/shared/components/Tabs";
import { Nav, Tab } from "react-bootstrap";
import CreateReport from "./CreateReport";
import SavedReports from "./SavedReports";
import styled from "styled-components";
import { scrollbarStyles } from "../../utils/palette";
import { left, right } from "../../utils/directions";
import { putApi } from "../../api/getUser";
import { postApi } from "../../api/getUser";
import { useDispatch, useSelector } from "react-redux";
import { FilterButton } from "../../shared/components/TableElements";
import {
  setMessages as setMessagesState,
  setConversationId as setConversationIdState,
  resetChat as resetChatAction,
} from "../../features/reportingChat/reportingChatSlice";
import InfoPopup from "../../shared/components/InfoPopup";
import PaymentProcessingPopup from "../../shared/components/PaymentProcessingPopup";
import useQuery from "../../shared/custom-hooks/useQuery";

const ReportingTabs = () => {
  const { t } = useTranslation("common");
  const sidebarCollapse = useSelector((state) => state.sidebar.collapse);
  const [activeKey, setActiveKey] = useState("1");
  const [editReport, setEditReport] = useState(null);

  const [savedReports, setSavedReports] = useState([]);
  const [fetchReportsLoading, setFetchReportsLoading] = useState(false);

  // Success popup state for top-up credits
  const urlQuery = useQuery();
  const status = urlQuery.get("status");
  const is_topup = urlQuery.get("is_topup");

  // Payment processing popup states
  const [showPaymentProcessingPopup, setShowPaymentProcessingPopup] = useState(false);
  const [paymentProcessingData, setPaymentProcessingData] = useState({
    message: t("payment_processing"),
    icon: { className: "fi fi-rs-loader", color: "blue" },
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const dispatch = useDispatch();
  const messages = useSelector((s) => s.reportingChat.messages);
  const conversationId = useSelector((s) => s.reportingChat.conversationId);

  // Local-only UI state
  const [userInput, setUserInput] = useState("");
  const [tableData, setTableData] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [showChat, setShowChat] = useState(true);

  // Derive query from the latest bot message
  const query = useMemo(() => {
    const latestBotMessage = messages
      .slice()
      .reverse()
      .find((msg) => msg.sender === "bot" && msg.query && msg.table);

    return latestBotMessage?.query || "";
  }, [messages]);

  //console.log("query", query);

  const setMessages = (updater) => {
    const next = typeof updater === "function" ? updater(messages) : updater;
    dispatch(setMessagesState(next));
  };
  const setConversationId = (value) => dispatch(setConversationIdState(value));

  const fetchReports = useCallback(async () => {
    setFetchReportsLoading(true);
    //setError(null);
    try {
      const res = await putApi(
        { querystring: "get_user_ai_reports" },
        "dashboard"
      );
      const data = Array.isArray(res?.data) ? res.data : [];
      setSavedReports(data);
    } catch (err) {
      console.error("Fetch error:", err);
      //setError("Something went wrong while fetching reports.");
    } finally {
      setFetchReportsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Auto switch to Create tab if SavedReports won't render
  useEffect(() => {
    if (savedReports.length === 0 && activeKey !== "2") {
      setActiveKey("2");
    }
  }, [savedReports, activeKey]);

  // Effect to verify payment for is_topup
  useEffect(() => {
    const sessionId = urlQuery.get("session_id");

    if (!sessionId || !is_topup || !status) return;

    // Check status first before verifyPayment
    if (status === "failed") {
      setPaymentProcessingData({
        message: t("payment_failed"),
        icon: { className: "fi fi-rr-cross-circle", color: "red" },
      });
      setIsProcessing(false);
      setShowPaymentProcessingPopup(true);
      return;
    }

    // Only proceed with verifyPayment if status is success or not provided
    if (status && status !== "success") return;

    const verifyPayment = async (attempt = 0) => {
      try {
        const res = await postApi(
          { querystring: "verify_payment", session_id: sessionId },
          "subscription"
        );
        console.log("verifyPayment res", res);

        if (res.success) {
          const { data } = res;
          const { payment_status, payment_intent_status, last_payment_error } = data;

          // Case 1: Payment is successful
          if (
            payment_status === "paid" ||
            payment_intent_status === "succeeded"
          ) {
            setPaymentProcessingData({
              message: t("top_up_credits_success_msg"),
              icon: { className: "fi fi-rs-check-circle", color: "green" },
            });
            setIsProcessing(false);
            setShowPaymentProcessingPopup(true);
            return;
          }

          // Case 2: Payment failed
          if (
            payment_status === "failed" ||
            payment_intent_status === "requires_payment_method"
          ) {
            setPaymentProcessingData({
              message: last_payment_error || t("payment_failed"),
              icon: { className: "fi fi-rr-cross-circle", color: "red" },
            });
            setIsProcessing(false);
            setShowPaymentProcessingPopup(true);
            return;
          }

          // Case 3: Payment is processing
          if (payment_intent_status === "processing") {
            if (attempt < 3) {
              // Show loader and processing message, then retry
              setPaymentProcessingData({
                message: t("payment_processing"),
                icon: { className: "fi fi-rs-loader", color: "blue" },
              });
              setIsProcessing(true);
              setShowPaymentProcessingPopup(true);

              // Retry after 5 seconds
              setTimeout(() => verifyPayment(attempt + 1), 5000);
            } else {
              // After 3 attempts, show generic processing message without loader
              setPaymentProcessingData({
                message: t("payment_processing_generic"),
                icon: { className: "fi fi-rs-clock", color: "orange" },
              });
              setIsProcessing(false);
              setShowPaymentProcessingPopup(true);
            }
            return;
          }

          // Case 4: Any other payment_intent_status - show failure
          setPaymentProcessingData({
            message: t("payment_verification_failed"),
            icon: { className: "fi fi-rr-cross-circle", color: "red" },
          });
          setIsProcessing(false);
          setShowPaymentProcessingPopup(true);
        } else {
          // API call failed
          setPaymentProcessingData({
            message: t("payment_verification_failed"),
            icon: { className: "fi fi-rr-cross-circle", color: "red" },
          });
          setIsProcessing(false);
          setShowPaymentProcessingPopup(true);
        }
      } catch (err) {
        console.error("Payment verification error:", err);
        setPaymentProcessingData({
          message: t("payment_verification_failed"),
          icon: { className: "fi fi-rr-cross-circle", color: "red" },
        });
        setIsProcessing(false);
        setShowPaymentProcessingPopup(true);
      }
    };

    // Start payment verification
    verifyPayment();
  }, [is_topup, urlQuery, status, t]);

  const onEditReport = (report) => {
    console.log("report", report);
    setEditReport({
      reportId: report.report_id,
      reportName: report.report_name,
      conversationId: report.conversation_id,
    });
    setActiveKey("2");
  };

  const clearEditReport = () => setEditReport(null);

  const resetChat = () => {
    clearEditReport();
    dispatch(resetChatAction());
    // Seed welcome message immediately so UI shows guidance without remount
    dispatch(
      setMessagesState([
        {
          sender: "bot",
          text: t("create_report_bot_welcome_msg"),
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ])
    );
    setUserInput("");
    setTableData([]);
    setHasMore(false);
    // Reset showChat to true when resetting the chat
    setShowChat(true);
  };

  // Derive chatStarted from persisted messages
  //chatStarted should also be true if a user comes from edit report
  const chatStarted = useMemo(() => {
    if (!Array.isArray(messages)) return false;
    return (
      messages.length > 1 ||
      messages.some(
        (m) =>
          m?.sender === "user" ||
          (m?.table && (Array.isArray(m.table) ? m.table.length > 0 : true)) ||
          editReport
      )
    );
  }, [messages, editReport]);

  const tabs = [
    ...(savedReports.length > 0
      ? [
          {
            key: "1",
            label: t("saved_reports"),
            content: (
              <SavedReports
                activeKey={activeKey}
                onEditReport={onEditReport}
                savedReports={savedReports}
                fetchReportsLoading={fetchReportsLoading}
                fetchReports={fetchReports}
              />
            ),
          },
        ]
      : []),
    {
      key: "2",
      label: t("create_report"),
      content: (
        <CreateReport
          activeKey={activeKey}
          editReport={editReport}
          clearEditReport={clearEditReport}
          fetchReports={fetchReports}
          setActiveKey={setActiveKey}
          messages={messages}
          setMessages={setMessages}
          userInput={userInput}
          setUserInput={setUserInput}
          query={query}
          conversationId={conversationId}
          setConversationId={setConversationId}
          tableData={tableData}
          setTableData={setTableData}
          hasMore={hasMore}
          setHasMore={setHasMore}
          showChat={showChat}
          setShowChat={setShowChat}
        />
      ),
    },
  ];

  return (
    <Tab.Container
      defaultActiveKey="1"
      activeKey={activeKey}
      onSelect={(e) => setActiveKey(e)}
    >
      <TabsWrap
      // style={{
      //   width: sidebar?.collapse
      //     ? window.innerWidth - 180
      //     : window.innerWidth - 370,
      // }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Nav className="nav-tabs">
              {tabs.map((tab) => (
                <NavItem key={tab.key} $active={activeKey === tab.key}>
                  <NavLink eventKey={tab.key}>{tab.label}</NavLink>
                </NavItem>
              ))}
            </Nav>
          </div>
          {chatStarted && activeKey == "2" && (
            <FilterButton className="mx-3" onClick={resetChat}>
              <div className="d-flex align-items-center gap-2">
                <i
                  className="fi fi-rr-refresh"
                  style={{ margin: 0, position: "relative", top: "1px" }}
                ></i>
                <p>{t("new_report")}</p>
              </div>
            </FilterButton>
          )}
        </div>
        <Tab.Content>
          {tabs.map((tab) => (
            <TabPane key={tab.key} eventKey={tab.key}>
              {tab.content}
            </TabPane>
          ))}
          {/* <TabPane eventKey="1">
            <SavedReports
              activeKey={activeKey}
              onEditReport={onEditReport}
              savedReports={savedReports}
              fetchReportsLoading={fetchReportsLoading}
            />
          </TabPane>
          <TabPane eventKey="2">
            <CreateReport
              activeKey={activeKey}
              editReport={editReport}
              clearEditReport={clearEditReport}
              fetchReports={fetchReports}
            />
          </TabPane> */}
        </Tab.Content>

        {activeKey === "2" && (
          <FixedFooter $sidebarCollapse={sidebarCollapse}>
            {t("reporting_uses_ai_msg")}
          </FixedFooter>
        )}
      </TabsWrap>

      {showPaymentProcessingPopup && (
        <PaymentProcessingPopup
          showPopup={showPaymentProcessingPopup}
          setShowPopup={setShowPaymentProcessingPopup}
          message={paymentProcessingData?.message}
          icon={paymentProcessingData?.icon}
          isLoading={isProcessing}
          processingMessage={t("payment_processing")}
          actionIcon={{
            handler: () => setShowPaymentProcessingPopup(false),
            label: t("ok"),
          }}
        />
      )}
    </Tab.Container>
  );
};

export default ReportingTabs;

//styles
const TabPane = styled(Tab.Pane)`
  ${scrollbarStyles};
  //height: ${window.innerHeight - 172}px;
  height: calc(var(--window-height, 100vh) - 172px);
  overflow: auto;
  //padding: 1rem;
`;

const FixedFooter = styled.div`
  position: fixed;
  ${left}: ${(p) =>
    p.theme.direction === "rtl" ? "0" : p.$sidebarCollapse ? "0" : "250px"};
  ${right}: 0;
  bottom: 0;
  padding: 6px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  text-align: center;
  font-size: 12px;
  color: #6b7280;
  z-index: 10;
`;
